import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { APPOINTMENTS_FOR_PET_QUERY, VETERINARIAN_USERS_QUERY } from '@/modules/appointments/appointments.gql';
import { CUSTOMER_QUERY } from '@/modules/customers-pets/customers.gql';
import { PET_QUERY } from '@/modules/customers-pets/pets.gql';
import { PET_SEX_FROM_GQL } from '@/shared/graphql/enums';

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return */

const UPCOMING_APPOINTMENT_STATUSES = new Set(['booked', 'confirmed', 'checked_in', 'in_progress']);

export interface RxPrintBaseInfo {
  patient_name: string;
  species: string | null;
  breed: string | null;
  sex_label: string | null;
  age_label: string | null;
  pet_record_code: string | null;
  owner_name: string | null;
  owner_phone: string | null;
  next_appointment_label: string | null;
  vet_name: string;
  vet_license: string | null;
}

/** Whole months between a birth date and today; never negative. */
function monthsSinceBirth(birth_date: string): number | null {
  const parsed = new Date(`${birth_date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  const now = new Date();
  let months = (now.getFullYear() - parsed.getFullYear()) * 12 + (now.getMonth() - parsed.getMonth());
  if (now.getDate() < parsed.getDate()) months -= 1;
  return Math.max(0, months);
}

function formatAgeLabel(approximate_age_months: number | null, birth_date: string | null): string | null {
  const total_months = approximate_age_months ?? (birth_date ? monthsSinceBirth(birth_date) : null);
  if (total_months == null) return null;
  const years = Math.floor(total_months / 12);
  const rem_months = total_months % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? 'año' : 'años'}`);
  if (rem_months > 0 || years === 0) parts.push(`${rem_months} ${rem_months === 1 ? 'mes' : 'meses'}`);
  return parts.join(' ');
}

/** Grams to a printable "12.40 kg" label; null when no weight was captured. */
export function formatWeightLabel(grams: number | null | undefined): string | null {
  if (grams == null) return null;
  return `${(grams / 1000).toFixed(2)} kg`;
}

function findNextAppointment(items: any[]): string | null {
  const now = Date.now();
  const upcoming = items
    .filter((a) => UPCOMING_APPOINTMENT_STATUSES.has(a.status) && new Date(a.scheduled_start_at).getTime() >= now)
    .sort((a, b) => new Date(a.scheduled_start_at).getTime() - new Date(b.scheduled_start_at).getTime());
  return upcoming.length > 0 ? upcoming[0].scheduled_start_at : null;
}

/**
 * Assembles the header/footer data for a printed prescription: breed/sex/age/owner come from the
 * pet + customer records (not the SOAP visit itself), the attending vet's name+license are resolved
 * from `visit.veterinarian_id` (NOT the current session user) so a reprint from history always shows
 * who actually prescribed it, not whoever is viewing the record now.
 */
export function useRxPrintBase(args: {
  pet_id: string | null;
  veterinarian_id: string | null;
  pet_name: string | null;
  species: string | null;
  pet_record_code: string | null;
}): RxPrintBaseInfo {
  const { t } = useTranslation();

  const { data: pet_data } = useQuery(PET_QUERY, {
    variables: { id: args.pet_id ?? '' },
    skip: !args.pet_id,
    fetchPolicy: 'cache-first',
  });
  const pet = pet_data?.pet ?? null;

  const { data: customer_data } = useQuery(CUSTOMER_QUERY, {
    variables: { id: pet?.customer_id ?? '' },
    skip: !pet?.customer_id,
    fetchPolicy: 'cache-first',
  });
  const customer = customer_data?.customer ?? null;

  const { data: appt_data } = useQuery(APPOINTMENTS_FOR_PET_QUERY, {
    variables: { pet_id: args.pet_id ?? '' },
    skip: !args.pet_id,
    fetchPolicy: 'cache-first',
  });
  const next_appointment_at = findNextAppointment(appt_data?.appointmentsForPet ?? []);

  const { data: vets_data } = useQuery(VETERINARIAN_USERS_QUERY, { fetchPolicy: 'cache-first' });
  const vet = (vets_data?.veterinarianUsers ?? []).find((v: any) => v.id === args.veterinarian_id) ?? null;

  const sex_key = pet?.sex ? PET_SEX_FROM_GQL[pet.sex as keyof typeof PET_SEX_FROM_GQL] : null;
  const sex_label =
    sex_key === 'male' ? t('pets.sex_male') : sex_key === 'female' ? t('pets.sex_female') : null;

  return {
    patient_name: args.pet_name ?? '—',
    species: args.species ?? null,
    breed: pet?.breed ?? null,
    sex_label,
    age_label: formatAgeLabel(pet?.approximate_age_months ?? null, pet?.birth_date ?? null),
    pet_record_code: args.pet_record_code ?? null,
    owner_name: customer ? `${customer.first_name}${customer.last_name ? ` ${customer.last_name}` : ''}` : null,
    owner_phone: customer?.phone ?? null,
    next_appointment_label: next_appointment_at ? new Date(next_appointment_at).toLocaleDateString() : null,
    vet_name: vet?.full_name ?? '—',
    vet_license: vet?.professional_license ?? null,
  };
}
